import React, { useState, useEffect, useCallback } from 'react';
import { 
  EnvelopeIcon, 
  PlusIcon, 
  PaperAirplaneIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Email, EmailFilters, EmailStats, SmtpSettings } from '../types/Email';
import { emailApi } from '../services/emailApi';
import EmailList from './EmailList';
import ComposeEmailModal from './ComposeEmailModal';
import EmailStatsCard from './EmailStatsCard';
import Layout from './Layout';
import { useBranch } from '../contexts/BranchContext';
import PermissionGate from './PermissionGate';

const EmailComponent: React.FC = () => {
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [draftEmail, setDraftEmail] = useState<Email | null>(null);
  const [smtpSettings, setSmtpSettings] = useState<SmtpSettings | null>(null);
  const [emailStats, setEmailStats] = useState<EmailStats | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 20,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [filters, setFilters] = useState<EmailFilters>({
    page: 1,
    limit: 20,
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });

  const fetchEmails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await emailApi.getEmails(filters);
      setEmails(response.emails);
      setPagination(response.pagination);
    } catch (err) {
      console.error('Error fetching emails:', err);
      setError('Failed to fetch emails');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchSmtpSettings = useCallback(async () => {
    try {
      const settings = await emailApi.getActiveSmtpSettings();
      setSmtpSettings(settings);
    } catch (err) {
      console.error('Error fetching SMTP settings:', err);
    }
  }, []);

  const fetchEmailStats = useCallback(async () => {
    try {
      const stats = await emailApi.getEmailStats();
      setEmailStats(stats);
    } catch (err) {
      console.error('Error fetching email stats:', err);
    }
  }, []);

  useEffect(() => {
    if (!branchLoading) {
      fetchEmails();
    }
  }, [fetchEmails, currentBranch, isViewingAllBranches, branchLoading]);

  useEffect(() => {
    fetchSmtpSettings();
    fetchEmailStats();
  }, [fetchSmtpSettings, fetchEmailStats]);

  const handleFilterChange = (newFilters: Partial<EmailFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleSort = (sortBy: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy,
      sortOrder: prev.sortBy === sortBy && prev.sortOrder === 'ASC' ? 'DESC' : 'ASC'
    }));
  };

  const handleEmailSent = () => {
    fetchEmails();
    fetchEmailStats();
    setShowComposeModal(false);
  };

  const handleEmailDeleted = (emailId: number) => {
    setEmails(prev => prev.filter(email => email.id !== emailId));
    fetchEmailStats();
  };


  const handleViewEmail = (email: Email) => {
    setSelectedEmail(email);
  };

  const handleCloseEmailDetail = () => {
    setSelectedEmail(null);
  };

  const handleEditDraft = (email: Email) => {
    // Set the draft email data and open compose modal
    setDraftEmail(email);
    setShowComposeModal(true);
  };

  const handleSendDraft = async (email: Email) => {
    try {
      setLoading(true);
      // Send the draft email
      await emailApi.sendDraft(email.id);
      // Refresh the email list
      await fetchEmails();
      // Show success message
      alert('Draft email sent successfully!');
    } catch (error) {
      console.error('Error sending draft:', error);
      alert('Failed to send draft email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Layout title="Email" subtitle="Manage and send emails to your leads">
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <PermissionGate module="emails" permission="create">
            <button
              onClick={() => setShowComposeModal(true)}
              disabled={!smtpSettings}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Compose Email
            </button>
          </PermissionGate>
        </div>

        {/* SMTP Warning */}
        {!smtpSettings && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  SMTP Settings Required
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>Please configure your SMTP settings to send emails.</p>
                </div>
                <div className="mt-4">
                  <p className="text-sm text-yellow-700">
                    Please configure SMTP settings in Settings → Email Settings to send emails.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        {emailStats && (
          <div>
            <EmailStatsCard stats={emailStats} />
          </div>
        )}

        {/* Email List */}
        <div className="bg-white shadow rounded-lg">
          <EmailList
            emails={emails}
            loading={loading}
            error={error}
            pagination={pagination}
            filters={filters}
            onFilterChange={handleFilterChange}
            onPageChange={handlePageChange}
            onSort={handleSort}
            onViewEmail={handleViewEmail}
            onDeleteEmail={handleEmailDeleted}
            onEditDraft={handleEditDraft}
            onSendDraft={handleSendDraft}
          />
        </div>
      </div>
    </Layout>

    {/* Modals */}
      {showComposeModal && (
        <ComposeEmailModal
          onClose={() => {
            setShowComposeModal(false);
            setDraftEmail(null);
          }}
          onEmailSent={handleEmailSent}
          smtpSettings={smtpSettings}
          draftEmail={draftEmail}
        />
      )}


    {selectedEmail && (
      <EmailDetailModal
        email={selectedEmail}
        onClose={handleCloseEmailDetail}
        onDelete={handleEmailDeleted}
      />
    )}
    </>
  );
};

// Email Detail Modal Component
const EmailDetailModal: React.FC<{
  email: Email;
  onClose: () => void;
  onDelete: (emailId: number) => void;
}> = ({ email, onClose, onDelete }) => {
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this email?')) {
      try {
        await emailApi.deleteEmail(email.id);
        onDelete(email.id);
        onClose();
      } catch (error) {
        console.error('Error deleting email:', error);
        alert('Failed to delete email');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Email Details</h3>
          <div className="flex space-x-2">
            <button
              onClick={handleDelete}
              className="text-red-600 hover:text-red-800"
            >
              Delete
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Subject</label>
            <p className="mt-1 text-sm text-gray-900">{email.subject}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">From</label>
            <p className="mt-1 text-sm text-gray-900">{email.fromName} &lt;{email.fromEmail}&gt;</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">To</label>
            <p className="mt-1 text-sm text-gray-900">{email.toEmails.join(', ')}</p>
          </div>

          {email.ccEmails.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700">CC</label>
              <p className="mt-1 text-sm text-gray-900">{email.ccEmails.join(', ')}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              email.status === 'sent' ? 'bg-green-100 text-green-800' :
              email.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {email.status.charAt(0).toUpperCase() + email.status.slice(1)}
            </span>
          </div>

          {email.sentAt && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Sent At</label>
              <p className="mt-1 text-sm text-gray-900">
                {new Date(email.sentAt).toLocaleString()}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Message</label>
            <div className="mt-1 p-3 bg-gray-50 rounded-md">
              <div 
                className="text-sm text-gray-900 whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: email.htmlBody || email.body }}
              />
            </div>
          </div>

          {email.attachments && email.attachments.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Attachments</label>
              <div className="mt-1 space-y-2">
                {email.attachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center space-x-2">
                    <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{attachment.originalFilename}</span>
                    <span className="text-xs text-gray-500">
                      ({emailApi.formatFileSize(attachment.fileSize)})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailComponent;
